import { useEffect, useMemo, useRef, useState } from "react";

import styled from "styled-components/macro";

import { usePermissions } from "Flocknote/apiHooks";

import { useClearActiveAction } from "Dashboard/components/WhatsNext/whatsNextStore";
import { LinkListRowsWrapper } from "NavBar/components/LinkListRow";
import NavLink from "NavBar/components/NavLink";
import NavLinksWrapper from "NavBar/components/NavLinksWrapper";
import NavSection from "NavBar/components/NavSection";
import {
  navSectionKeys,
  useCreateSignupLinkRef,
  useRESetupLinkRef,
  useSignupsAndEventsRef,
} from "NavBar/navBarStore";
import {
  useIsCreateSignupButtonFlashing,
  useIsRESetupLinkFlashing,
  useIsSignupsAndEventsSquiggleFlashing,
} from "NavBar/stateHooks";
import { everyoneButtonHeight, navSectionGap } from "NavBar/styleConstants";

import { navColors } from "UI/colors";

import useHeight from "Utilities/Hooks/useHeight";
import dynHeight from "Utilities/interpolations/dynHeight";
import dynMinHeight from "Utilities/interpolations/dynMinHeight";

import { useHasActiveSignups } from "./API/queryHooks";
import FloatingButtonPanel from "./FloatingButtonPanel";
import NoSearchMatches from "./NoSearchMatches";
import SignupsList from "./SignupsList";
import { importSignupsAll } from "./prefetch";
import {
  useDebouncedSearchValue,
  useSearchContainerRef,
} from "./signupsAndEventsStore";

const SignupsAndEvents = () => {
  const [{ canRESetup }] = usePermissions();
  const hasSignups = useHasActiveSignups();
  const signupsAndEventsRef = useSignupsAndEventsRef();
  const createSignupLinkRef = useCreateSignupLinkRef();
  const reSetupLinkRef = useRESetupLinkRef();
  const isSquiggleFlashing = useIsSignupsAndEventsSquiggleFlashing();
  const isCreateSignupButtonFlashing = useIsCreateSignupButtonFlashing();
  const isRESetupLinkFlashing = useIsRESetupLinkFlashing();
  const clearActiveAction = useClearActiveAction();

  const debouncedSearchValue = useDebouncedSearchValue();
  const isSearching = debouncedSearchValue.length > 0;
  const searchContainerRef = useSearchContainerRef();
  const mainLinksWrapperRef = useRef();
  const scrollRef = useRef();

  const [heightAtStartOfSearch, setHeightAtStartOfSearch] = useState();

  const timeoutRef = useRef();
  useEffect(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (!scrollRef.current || !mainLinksWrapperRef.current) return;
      const newHeightAtStartOfSearch =
        scrollRef.current.clientHeight -
        mainLinksWrapperRef.current.clientHeight -
        navSectionGap * 2 -
        1 /* divider */ +
        8; /* sibling margins */
      setHeightAtStartOfSearch((prev) =>
        prev !== newHeightAtStartOfSearch ? newHeightAtStartOfSearch : prev
      );
    }, 300);
    return () => clearTimeout(timeoutRef.current);
  }, [isSearching]);
  const floatingButtonPanelRef = useRef();
  // observed, not read off the ref in render: that read forced a style pass
  // on every render of this section
  const floatingButtonPanelHeight = useHeight(floatingButtonPanelRef);
  const spaceForFloatingPanel =
    floatingButtonPanelHeight > 0
      ? /* subtract the NavSection flex-gap for a more snug fit */
        floatingButtonPanelHeight - 8
      : /* compensate a bit for the discounting of padding */
        everyoneButtonHeight + 8;

  const firstVisibleLink = useMemo(() => {
    return hasSignups ? "/signups/all" : "/signups/new";
  }, [hasSignups]);

  return (
    <NavSectionWrapper>
      <NavSection
        label="Signups and Events"
        icon="clipboard-check far"
        navSectionKey={navSectionKeys.signups}
        color={navColors.signups}
        data-cy="NavBar_SignupsAndEvents"
        headerTextRef={signupsAndEventsRef}
        isSquiggleFlashing={isSquiggleFlashing}
        bottomPadding={hasSignups ? 0 : 4}
        scrollRef={scrollRef}
        onHeaderMouseEnter={importSignupsAll}
        defaultRoute={firstVisibleLink}
      >
        {(canRESetup || !hasSignups) && (
          <SNavLinksWrapper ref={mainLinksWrapperRef} minWidth={140}>
            {hasSignups ? (
              <NavLink
                navSectionKey={navSectionKeys.signups}
                to="signups/all"
                icon="clipboard-check far"
                sideEffect={clearActiveAction}
                data-cy="NavBar_AllSignups"
              >
                All Signups
              </NavLink>
            ) : (
              <NavLink
                ref={createSignupLinkRef}
                isFlashing={isCreateSignupButtonFlashing}
                navSectionKey={navSectionKeys.signups}
                to="signups/new"
                icon="plus-circle fas"
                sideEffect={clearActiveAction}
                data-cy="NavBar_NewSignup"
              >
                New Signup
              </NavLink>
            )}
            {canRESetup && (
              <NavLink
                ref={reSetupLinkRef}
                isFlashing={isRESetupLinkFlashing}
                to="re-setup"
                data-cy="NavBar_RESetup"
                icon="chalkboard-user fad"
                navSectionKey={navSectionKeys.signups}
                sideEffect={clearActiveAction}
              >
                RE Registration
              </NavLink>
            )}
          </SNavLinksWrapper>
        )}
        {hasSignups && (
          <>
            <NoSearchMatches />
            <SLinkListRowsWrapper
              $minHeight={isSearching ? heightAtStartOfSearch : 0}
              ref={searchContainerRef}
              // hand-adjusted - please modify in the future if necessary
              $marginBottom={11}
            >
              <SignupsList accentColor={navColors.signups} />
            </SLinkListRowsWrapper>
            <SpaceForFloatingButtonPanel $height={spaceForFloatingPanel} />
          </>
        )}
      </NavSection>
      <FloatingButtonPanel ref={floatingButtonPanelRef} />
    </NavSectionWrapper>
  );
};

const SpaceForFloatingButtonPanel = styled.div`
  ${dynHeight}
`;

const NavSectionWrapper = styled.div`
  position: relative;
`;

const SLinkListRowsWrapper = styled(LinkListRowsWrapper)`
  gap: 1px;
  ${dynMinHeight}
  transition: min-height 300ms;
`;

const SNavLinksWrapper = styled(NavLinksWrapper)`
  margin: 1px 0 4px 0;
`;

export default SignupsAndEvents;
